/**
 * LearnPage：学习页（ziwei-knowledge 知识库客户端）
 * - 分类 tabs：目录 category 去重派生（服务端 categories 接口仅管理端，不新增公开接口）
 * - 章节树：parent_id 组树 + sort 排序，可展开折叠；pro 节点显 🔒 + 等级标签（basic 全员开放不显锁）
 * - 主从详情：桌面左树右详情；窄屏列表 ↔ 详情切换（返回经顶栏「← 返回」按钮）
 * - 权益：free/basic 直读（basic 全员开放）；pro 权益不足（40301）显 preview + 会员需知卡，本期不含订阅购买
 * - 缓存：目录 localStorage SWR（先显缓存后台刷新）；正文加密 IndexedDB 缓存（learnCache.ts，version 不变不重拉，
 *   付费正文 AES-GCM 密文 + 读时权益复核；阶段三 /membership/me 接入 ctx.memberLevel 后复核才真实生效）
 */
import { useEffect, useMemo, useState } from 'react';
import { MdPreview } from 'md-editor-rt';
import 'md-editor-rt/lib/preview.css';
import { useChartStore } from '../state/store';
import { useLearnStore } from '../state/learn-store';
import { useMembershipStore } from '../state/membership-store';
import { jwtUserId } from '@shared/api/membership';
import { fetchLearnChapter, fetchLearnChapters, MembershipRequiredError, rewriteContent } from '@shared/api/learn';
import type { LearnChapterSummary } from '@shared/api/learn';
import { getCachedChapter, putCachedChapter, deleteCachedChapter, purgePaidChapters, purgeLegacyPlainCache } from '@shared/api/learnCache';
import type { LearnCacheCtx } from '@shared/api/learnCache';
import { isNativeApp } from '@shared/api/screenGuard';
import { useTtsStore } from '../state/tts-store';
import TtsPlayerBar from '../components/TtsPlayerBar';

const LIST_CACHE_KEY = 'paipan.learn.chapters';
// 听书（TTS）为 App 独占能力：Web 端不渲染播放条
const IS_APP = isNativeApp();

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, val: string): void {
  try {
    localStorage.setItem(key, val);
  } catch {
    // 配额/隐私模式静默
  }
}

/** 权益等级展示文案 */
function levelLabel(level: string): string {
  if (level === 'pro') return '高级会员';
  if (level === 'basic') return '基础会员';
  return '';
}

/** 防盗水印（阶段二）：登录用户看付费内容时叠加平铺用户名+ID尾号，pointer-events-none 不碍阅读滚动 */
function Watermark({ text }: { text: string }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-10 overflow-hidden opacity-[0.09] select-none">
      <div className="absolute -inset-[60%] flex rotate-[-24deg] flex-col justify-center gap-[22vh] text-center text-[14px] text-slate-950">
        {Array.from({ length: 9 }, (_, i) => (
          <span key={i} className="whitespace-nowrap">
            {text}
          </span>
        ))}
      </div>
    </div>
  );
}

/** 组树：roots（parent_id=0 或父不在列表）+ 子映射；sort,id 排序 */
function buildTree(items: LearnChapterSummary[]) {
  const byId = new Set(items.map((i) => i.id));
  const children = new Map<number, LearnChapterSummary[]>();
  const roots: LearnChapterSummary[] = [];
  for (const it of items) {
    if (it.parent_id && byId.has(it.parent_id)) {
      const arr = children.get(it.parent_id) ?? [];
      arr.push(it);
      children.set(it.parent_id, arr);
    } else {
      roots.push(it);
    }
  }
  const cmp = (a: LearnChapterSummary, b: LearnChapterSummary) => a.sort - b.sort || a.id - b.id;
  roots.sort(cmp);
  for (const arr of children.values()) arr.sort(cmp);
  return { roots, children };
}

/** 章节树节点（递归） */
function TreeNode(props: {
  node: LearnChapterSummary;
  depth: number;
  childrenMap: Map<number, LearnChapterSummary[]>;
  expanded: Record<number, boolean>;
  selectedId: number | null;
  playingId: number | null;
  onToggle(id: number): void;
  onSelect(n: LearnChapterSummary): void;
}) {
  const { node, depth, childrenMap, expanded, selectedId, playingId, onToggle, onSelect } = props;
  const kids = childrenMap.get(node.id) ?? [];
  const open = !!expanded[node.id];
  const locked = node.effective_level === 'pro';
  const isPlaying = playingId != null && playingId === node.id;
  return (
    <li>
      <div
        data-chapter-id={node.id}
        className={`flex cursor-pointer items-center gap-1 rounded py-1.5 pr-2 hover:bg-slate-50 ${
          isPlaying ? 'bg-indigo-50' : selectedId === node.id ? 'bg-slate-100' : ''
        }`}
        style={{ paddingLeft: `${depth * 0.875 + 0.5}rem` }}
        onClick={() => onSelect(node)}
      >
        {kids.length > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            className="w-4 shrink-0 text-xs text-slate-400"
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span className={`flex-1 text-sm ${isPlaying ? 'font-medium text-indigo-700' : 'text-slate-700'}`}>{node.title}</span>
        {isPlaying && (
          <span className="mr-1 flex shrink-0 items-center gap-1 text-xs text-indigo-600">
            <span className="inline-flex items-end gap-0.5" aria-hidden>
              <i className="h-2 w-0.5 animate-pulse bg-indigo-500" />
              <i className="h-3 w-0.5 animate-pulse bg-indigo-500" style={{ animationDelay: '150ms' }} />
              <i className="h-1.5 w-0.5 animate-pulse bg-indigo-500" style={{ animationDelay: '300ms' }} />
            </span>
            播放中
          </span>
        )}
        {locked && <span className="shrink-0 text-xs text-amber-500">🔒 {levelLabel(node.effective_level)}</span>}
      </div>
      {open && kids.length > 0 && (
        <ul>
          {kids.map((k) => (
            <TreeNode key={k.id} {...props} node={k} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** 从 hash 解析当前详情章节 id（#/learn/chapter/:id → id；否则 null = 目录） */
function chapterIdFromHash(): number | null {
  const m = window.location.hash.match(/^#\/learn\/chapter\/(\d+)/);
  return m ? Number(m[1]) : null;
}

export default function LearnPage() {
  const token = useChartStore((s) => s.token);
  // 会员现状（App 启动已刷 me）：白名单放行复制/截屏，等级供加密缓存读时复核
  const memberMe = useMembershipStore((s) => s.me);
  const [items, setItems] = useState<LearnChapterSummary[] | null>(() => {
    const raw = safeGet(LIST_CACHE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LearnChapterSummary[];
    } catch {
      return null;
    }
  });
  const [listErr, setListErr] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  // 篇（分类）折叠状态：默认全部折叠，只列各篇标题（电子书目录）
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
  // 选中章节提升到 store：App 顶栏据此渲染「← 返回」
  const selectedId = useLearnStore((s) => s.selectedId);
  const setSelectedId = useLearnStore((s) => s.select);
  // 详情视图与 hash 子路由双向同步：#/learn/chapter/:id=详情，#/learn=目录；
  // 返回手势/前进后退改 hash 时由此切换（修复：文章详情左滑直接跳回排盘而非目录）
  useEffect(() => {
    const sync = () => {
      const id = chapterIdFromHash();
      if (useLearnStore.getState().selectedId !== id) setSelectedId(id);
    };
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, [setSelectedId]);
  const [detailStatus, setDetailStatus] = useState<'idle' | 'loading' | 'ready' | 'locked' | 'error'>('idle');
  const [detailContent, setDetailContent] = useState('');
  const [detailErr, setDetailErr] = useState('');
  const [retryTick, setRetryTick] = useState(0);

  // 一次性清理升级前的明文 localStorage 正文缓存（含旧付费正文明文落盘残留）
  useEffect(() => {
    purgeLegacyPlainCache();
  }, []);

  // 缓存上下文：memberLevel 取 me 真实等级（未登录/未返回=null → 跳过读时复核，靠 403 批量兜底）
  const cacheCtx = useMemo<LearnCacheCtx>(() => ({ token, memberLevel: memberMe?.level ?? null }), [token, memberMe?.level]);

  // 目录 SWR：先显缓存，后台刷新
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { items: fresh } = await fetchLearnChapters(token);
        if (cancelled) return;
        setItems(fresh);
        setListErr('');
        safeSet(LIST_CACHE_KEY, JSON.stringify(fresh));
      } catch (e) {
        if (!cancelled) setListErr((prev) => prev || (e instanceof Error ? e.message : String(e)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // 目录（TOC）：每个分类=一篇，篇内 parent_id 组树；合并 childrenMap 供点选展开用
  const toc = useMemo(() => {
    const list = items ?? [];
    const cats = [...new Set(list.map((i) => i.category))];
    const sections = cats.map((cat) => {
      const inCat = list.filter((i) => i.category === cat);
      const t = buildTree(inCat);
      return { cat, roots: t.roots, children: t.children, count: inCat.length };
    });
    const childrenMap = new Map<number, LearnChapterSummary[]>();
    for (const s of sections) for (const [k, v] of s.children) childrenMap.set(k, v);
    return { sections, childrenMap };
  }, [items]);
  // 选中章节所在篇自动展开（返回/深链进入详情时保持目录上下文）
  useEffect(() => {
    if (selectedId == null) return;
    const cat = (items ?? []).find((i) => i.id === selectedId)?.category;
    if (cat) setOpenCats((p) => (p[cat] ? p : { ...p, [cat]: true }));
  }, [selectedId, items]);
  const selected = useMemo(() => (items ?? []).find((i) => i.id === selectedId) ?? null, [items, selectedId]);

  // 正在播放的章节（锁屏/切后台回来后高亮定位）：有在播/暂停章且未停止时为其 id
  const ttsChapterId = useTtsStore((s) => s.chapterId);
  const ttsStatus = useTtsStore((s) => s.status);
  const playingId = ttsChapterId != null && ttsStatus !== 'idle' ? ttsChapterId : null;

  // 进入列表：自动展开正在播章节所属分类并滚动到可见
  useEffect(() => {
    if (playingId == null || !items) return;
    const cat = items.find((i) => i.id === playingId)?.category;
    if (cat) setOpenCats((p) => (p[cat] ? p : { ...p, [cat]: true }));
    const t = setTimeout(() => {
      document.querySelector(`[data-chapter-id="${playingId}"]`)?.scrollIntoView({ block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [playingId, items]);

  // 听书线性阅读顺序：按目录（篇=分类，篇内 parent_id 组树、sort,id 排序）深度优先展开为章 id 序列，供「连读/锁屏下一章」推进
  const readingOrder = useMemo(() => {
    const ids: number[] = [];
    const walk = (nodes: LearnChapterSummary[]) => {
      for (const n of nodes) {
        ids.push(n.id);
        const kids = toc.childrenMap.get(n.id) ?? [];
        if (kids.length > 0) walk(kids);
      }
    };
    for (const s of toc.sections) walk(s.roots);
    return ids;
  }, [toc]);

  // 把线性顺序 + 全量摘要 + 权益上下文注入 TTS 控制器（App 独占；仅 set，安全可重复）
  useEffect(() => {
    if (!IS_APP) return;
    useTtsStore.getState().configure({ order: readingOrder, summaries: items ?? [], token, memberLevel: memberMe?.level ?? null });
  }, [readingOrder, items, token, memberMe?.level]);

  // 防盗层开关（阶段二）：白名单放行全部可读；否则非 free 内容禁复制/另存 + 登录用户叠加水印
  //（未登录也可看 basic，无身份可标识→不打水印；me 未返回时默认保护）
  const allowCapture = memberMe?.allow_capture ?? false;
  const protect = !allowCapture && !!selected && selected.effective_level !== 'free';
  const wmText = (() => {
    const uid = jwtUserId(token);
    const name = memberMe?.nickname || memberMe?.username || '';
    const tail = uid != null ? `#${String(uid).slice(-6)}` : '';
    return [name, tail].filter(Boolean).join(' · ');
  })();
  const blockEvent = (e: React.SyntheticEvent) => {
    if (protect) e.preventDefault();
  };

  // 详情：加密缓存命中直显（版本比对+权益复核在 learnCache 内）；未命中拉取后写缓存；40301 → 锁态+删缓存
  useEffect(() => {
    if (!selected) {
      setDetailStatus('idle');
      return;
    }
    let cancelled = false;
    setDetailStatus('loading');
    setDetailErr('');
    void (async () => {
      const cached = await getCachedChapter(selected.id, selected.version, cacheCtx);
      if (cancelled) return;
      if (cached != null) {
        setDetailContent(cached);
        setDetailStatus('ready');
        return;
      }
      try {
        const d = await fetchLearnChapter(selected.id, token);
        if (cancelled) return;
        setDetailContent(d.content);
        setDetailStatus('ready');
        void putCachedChapter({ id: selected.id, version: selected.version, effective_level: selected.effective_level }, d.content, cacheCtx);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof MembershipRequiredError) {
          // 权益不足：该章缓存已不可信（单章删）+ 全部付费条目批量失效兜底（§3.3-2，等级刚过期/降级）
          void deleteCachedChapter(selected.id);
          void purgePaidChapters();
          setDetailStatus('locked');
        } else {
          setDetailErr(e instanceof Error ? e.message : String(e));
          setDetailStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, token, retryTick, cacheCtx]);

  function onSelect(n: LearnChapterSummary) {
    // 详情走 hash 子路由：产生 WebView 历史条目，返回手势/按钮可逐层回退（见 App onBackPressed）
    if (chapterIdFromHash() !== n.id) window.location.hash = `#/learn/chapter/${n.id}`;
    // 有子章节的节点点选同时展开（折叠仅经 ▸/▾）
    if ((toc.childrenMap.get(n.id) ?? []).length > 0) setExpanded((p) => ({ ...p, [n.id]: true }));
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      {/* 左：电子书式目录（篇=分类，可展开；窄屏打开详情时隐藏） */}
      <aside className={`w-full shrink-0 sm:w-80 ${selected ? 'max-sm:hidden' : ''}`}>
        <div className="rounded-lg bg-white p-2 shadow">
          {items === null && !listErr && <div className="py-10 text-center text-sm text-slate-400">加载中…</div>}
          {listErr && items === null && (
            <div className="px-4 py-10 text-center text-sm text-red-500">知识库加载失败：{listErr}</div>
          )}
          {items !== null && toc.sections.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-400">暂无内容</div>
          )}
          {items !== null &&
            toc.sections.map((s) => {
              const open = !!openCats[s.cat];
              return (
                <div key={s.cat} className="border-b border-slate-100 last:border-0">
                  <button
                    type="button"
                    onClick={() => setOpenCats((p) => ({ ...p, [s.cat]: !p[s.cat] }))}
                    className="flex w-full items-center gap-1 rounded py-2.5 pl-2 pr-2 text-left hover:bg-slate-50"
                  >
                    <span className="w-4 shrink-0 text-xs text-slate-400">{open ? '▾' : '▸'}</span>
                    <span className="flex-1 text-sm font-semibold text-slate-800">{s.cat}</span>
                    <span className="shrink-0 text-xs text-slate-400">{s.count}</span>
                  </button>
                  {open && (
                    <ul className="pb-1">
                      {s.roots.map((n) => (
                        <TreeNode
                          key={n.id}
                          node={n}
                          depth={0}
                          childrenMap={s.children}
                          expanded={expanded}
                          selectedId={selectedId}
                          playingId={playingId}
                          onToggle={(id) => setExpanded((p) => ({ ...p, [id]: !p[id] }))}
                          onSelect={onSelect}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
        </div>
      </aside>

      {/* 右：详情（窄屏未选章节时隐藏） */}
      <section className={`min-w-0 flex-1 ${selected ? '' : 'max-sm:hidden'}`}>
        <div className="relative rounded-lg bg-white p-4 shadow">
          {!selected && <div className="py-16 text-center text-sm text-slate-400">选择左侧章节开始学习</div>}
          {selected && (
            <>
              <h2 className="mb-3 text-base font-semibold text-slate-800">{selected.title}</h2>
              {IS_APP && detailStatus === 'ready' && (
                <TtsPlayerBar chapterId={selected.id} title={selected.title} contentMd={detailContent} />
              )}
              {detailStatus === 'loading' && <div className="py-10 text-center text-sm text-slate-400">加载中…</div>}
              {detailStatus === 'ready' && (
                <div
                  className={protect ? 'content-protect select-none' : ''}
                  onContextMenu={blockEvent}
                  onCopy={blockEvent}
                  onCut={blockEvent}
                  onDragStart={blockEvent}
                >
                  <MdPreview modelValue={rewriteContent(detailContent)} />
                </div>
              )}
              {detailStatus === 'locked' && (
                <>
                  <div
                    className={protect ? 'content-protect select-none' : ''}
                    onContextMenu={blockEvent}
                    onCopy={blockEvent}
                    onCut={blockEvent}
                    onDragStart={blockEvent}
                  >
                    <MdPreview modelValue={rewriteContent(selected.preview)} />
                  </div>
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    本章为「{levelLabel(selected.effective_level) || selected.effective_level}」内容，
                    {token ? '当前订阅档位不足。' : '请先登录后订阅会员。'}
                    <a href="#/membership" className="ml-1 font-medium text-indigo-600 underline">
                      去订阅会员 →
                    </a>
                  </div>
                </>
              )}
              {detailStatus === 'error' && (
                <div className="py-10 text-center text-sm text-red-500">
                  加载失败：{detailErr}
                  <button
                    type="button"
                    onClick={() => setRetryTick((t) => t + 1)}
                    className="ml-2 rounded bg-slate-700 px-2 py-1 text-xs text-white"
                  >
                    重试
                  </button>
                </div>
              )}
            </>
          )}
          {/* 防盗水印：登录用户 + 保护态时叠加（ready/locked 都覆盖） */}
          {protect && !!token && !!wmText && <Watermark text={wmText} />}
        </div>
      </section>
    </div>
  );
}
